// Import the GeoJSON data dynamically to avoid TypeScript complexity issues
import { useEffect, useState } from 'react';

// Define layer configuration
export interface LayerConfig {
  id: string;
  name: string;
  type: string;
  visible: boolean;
  selectable: boolean;
  color: string;
  description: string;
}

export const layerConfigs: LayerConfig[] = [
  {
    id: 'combiner_boxes',
    name: 'Combiner Boxes',
    type: 'combiner_boxes',
    visible: true,
    selectable: false,
    color: '#3b82f6',
    description: 'Combiner box structures'
  },
  {
    id: 'inverter_base',
    name: 'Inverter Base',
    type: 'inverter_base',
    visible: true,
    selectable: true,
    color: '#8b5cf6',
    description: 'Selectable inverter base structures'
  },
  {
    id: 'ug_conduit',
    name: 'Underground Conduit',
    type: 'ug_conduit',
    visible: true,
    selectable: false,
    color: '#f59e0b',
    description: 'Underground conduit lines'
  },
  {
    id: 'rack_outlines',
    name: 'Rack Outlines',
    type: 'rack_outlines',
    visible: false,
    selectable: false,
    color: '#6b7280',
    description: 'Solar panel rack structures'
  },
  {
    id: 'inverter_boxed',
    name: 'Inverter Boxes',
    type: 'inverter_boxed',
    visible: false,
    selectable: false,
    color: '#ec4899',
    description: 'Inverter box structures'
  },
  {
    id: 'site_layout_line',
    name: 'Site Layout Lines',
    type: 'site_layout_line',
    visible: false,
    selectable: false,
    color: '#ef5350',
    description: 'Site boundary and layout lines'
  },
  {
    id: 'site_polygon',
    name: 'Site Polygon',
    type: 'site_polygon',
    visible: false,
    selectable: false,
    color: '#10b981',
    description: 'Site boundary polygons'
  },
  {
    id: 'ug_conduit_34.5kv',
    name: 'UG Conduit 34.5kV',
    type: 'ug_conduit_34.5kv',
    visible: false,
    selectable: false,
    color: '#f97316',
    description: 'High voltage underground conduit'
  },
  {
    id: 'ug_conduit_34.5kv_1ckt',
    name: 'UG Conduit 34.5kV 1-Circuit',
    type: 'ug_conduit_34.5kv_1ckt',
    visible: false,
    selectable: false,
    color: '#eab308',
    description: 'Single circuit high voltage conduit'
  },
  {
    id: 'combiner_outlines',
    name: 'Combiner Outlines',
    type: 'combiner_outlines',
    visible: true,
    selectable: true,
    color: '#17c480',
    description: 'Selectable combiner boxes for cross-filtering'
  }
];

// Hook to load GeoJSON data dynamically
export const useSiteGeoJSON = () => {
  const [geoJSON, setGeoJSON] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch('/enriched_geojson_20250903_151714.geojson');
        if (!response.ok) {
          throw new Error('Failed to load GeoJSON file');
        }
        const data = await response.json();
        setGeoJSON(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadGeoJSON();
  }, []);

  return { geoJSON, loading, error };
};

// Helper function to get layer config by type
export const getLayerConfig = (type: string): LayerConfig | undefined => {
  return layerConfigs.find(config => config.type === type);
};

// Helper function to get all visible layer types
export const getVisibleLayerTypes = (): string[] => {
  return layerConfigs.filter(config => config.visible).map(config => config.type);
};

// Helper function to get selectable layer types
export const getSelectableLayerTypes = (): string[] => {
  return layerConfigs.filter(config => config.selectable).map(config => config.type);
};